import React, { useState } from "react";
import { Box } from "@material-ui/core";
import { useHistory, Redirect } from "react-router-dom";
import { useIntl } from "react-intl";

import SignInLayout from "../../common/SignInLayout";
import { useStyles } from "./SignInScreen.styles";
import { SignupContextProvider } from "../../context/SignupContext";
import { CheckPhoneNumber } from "./components/CheckPhoneNumber";
import { OTP_MAX_SIZE, PIN_MAX_SIZE } from "../../settings/constants";
import { CheckOtp } from "./components/CheckOtp";
import { CreatePin } from "./components/CreatePin";
import { ConfirmPin } from "./components/ConfirmPin";
import { CheckPin } from "./components/CheckPin";
import { PageTitle } from "../../components/page-title/PageTitle";
import { SlideWrapper } from "../../components/slide-wrapper/SlideWrapper";
import { useAuthenticationContext } from "../../context/UserContext";
import { HomeScreen } from "../home/HomeScreen";
import ConnectionLog from "../../models/ConnectionLog";

export interface SignInStateAttributes {
  countryIsoCode: string;
  dialCode: string;
  phone: string;
  pin: string;
}

const initDataState = (): SignInStateAttributes => {
  return {
    countryIsoCode: "",
    dialCode: "",
    phone: "",
    pin: "",
  };
};

export const SignInScreen = (props: any) => {
  const classes = useStyles(props);
  const history = useHistory();
  const intl = useIntl();
  // State
  const [log, setLog] = useState((): ConnectionLog => null);
  const [step, setStep] = useState(1);
  const [data, setData] = useState(
    (): SignInStateAttributes => initDataState()
  );
  // Context
  const [authUser, token] = useAuthenticationContext();

  const sendOtpCodeCallback = (
    countryIsoCode: string,
    dialCode: string,
    phone: string
  ) => {
    // Set data
    let newData = {
      ...data,
      countryIsoCode: countryIsoCode,
      dialCode: dialCode,
      phone: phone,
    };
    setData(newData);

    // Next step
    setStep(2);
  };

  const checkOtpCodeCallback = (redirect: boolean, log: ConnectionLog) => {
    // Next step
    let step = redirect ? 5 : 3;
    setStep(step);
    setLog(log);
  };

  const createPinCallback = (pin: string) => {
    // Set data
    let newData = { ...data, pin: pin };
    setData(newData);

    // Next step
    setStep(4);
  };

  const checkPinCallback = () => {
    history.replace(HomeScreen.routeName);
  };

  if (Boolean(token !== null) && Boolean(authUser !== null)) {
    return <Redirect to={{ pathname: HomeScreen.routeName }} />;
  }

  return (
    <>
      <PageTitle title={intl.formatMessage({ id: "sharedScreen.signin" })} />
      <SignupContextProvider>
        <SignInLayout>
          <Box className={classes.root}>
            <Box component="div" className={classes.contentWrapper}>
              {step === 1 ? (
                <SlideWrapper>
                  <CheckPhoneNumber callback={sendOtpCodeCallback} />
                </SlideWrapper>
              ) : null}

              {step === 2 ? (
                <SlideWrapper>
                  <CheckOtp
                    data={data}
                    otpLength={OTP_MAX_SIZE}
                    callback={(redirect, log) =>
                      checkOtpCodeCallback(redirect, log)
                    }
                  />
                </SlideWrapper>
              ) : null}

              {step === 3 ? (
                <SlideWrapper>
                  <CreatePin
                    pinLength={PIN_MAX_SIZE}
                    callback={createPinCallback}
                  />
                </SlideWrapper>
              ) : null}

              {step === 4 ? (
                <SlideWrapper>
                  <ConfirmPin pinLength={PIN_MAX_SIZE} data={data} />
                </SlideWrapper>
              ) : null}

              {step === 5 ? (
                <SlideWrapper>
                  <CheckPin
                    pinLength={PIN_MAX_SIZE}
                    data={data}
                    log={log}
                    callback={checkPinCallback}
                  />
                </SlideWrapper>
              ) : null}
            </Box>
          </Box>
        </SignInLayout>
      </SignupContextProvider>
    </>
  );
};

SignInScreen.routeName = "/signin";
